import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type {
  SurveyAnswerValue,
  SurveyParticipantSnapshot,
  SurveyResponse,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * Survey response 一覧 hook (GAP-A10、Sprint BE)。
 *
 * 認可: firestore.rules で master / district admin (担当地区) のみ read 可能 (本人 read は別 path)。
 * 本 hook は admin 用集計閲覧専用 → onSnapshot で reactive。
 *
 * 旧 v1 lib/surveyResponses.ts の getDocs(...) ベース取得を v2 で reactive 化。
 */

export type SurveyResponsesState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: SurveyResponse[]; error: null }
  | { status: 'error'; items: []; error: Error };

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asProfile(value: unknown): SurveyParticipantSnapshot {
  const v = (value ?? {}) as Record<string, unknown>;
  return {
    nickname: typeof v.nickname === 'string' ? v.nickname : '',
    ageGroup: typeof v.ageGroup === 'string' ? v.ageGroup : null,
    gender: typeof v.gender === 'string' ? v.gender : null,
    relationship: typeof v.relationship === 'string' ? v.relationship : null,
    attribute: typeof v.attribute === 'string' ? v.attribute : null,
    joinedSessions: Array.isArray(v.joinedSessions)
      ? (v.joinedSessions as unknown[])
          .filter((s) => s && typeof s === 'object')
          .map((s) => {
            const r = s as Record<string, unknown>;
            return {
              sessionId: typeof r.sessionId === 'string' ? r.sessionId : '',
              name: typeof r.name === 'string' ? r.name : '',
              code: typeof r.code === 'string' ? r.code : '',
            };
          })
      : [],
  };
}

function asAnswers(value: unknown): Record<string, SurveyAnswerValue> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, SurveyAnswerValue> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (!v || typeof v !== 'object') continue;
    const a = v as Record<string, unknown>;
    const t = a.type;
    if (t === 'text' && typeof a.value === 'string') {
      out[k] = { type: 'text', value: a.value };
    } else if (t === 'choice' && typeof a.value === 'string') {
      out[k] = {
        type: 'choice',
        value: a.value,
        ...(typeof a.otherText === 'string' ? { otherText: a.otherText } : {}),
      };
    } else if (t === 'choices' && Array.isArray(a.values)) {
      out[k] = {
        type: 'choices',
        values: (a.values as unknown[]).filter((x): x is string => typeof x === 'string'),
        ...(typeof a.otherText === 'string' ? { otherText: a.otherText } : {}),
      };
    } else if (t === 'number' && typeof a.value === 'number') {
      out[k] = { type: 'number', value: a.value };
    } else if (t === 'date' && typeof a.value === 'string') {
      out[k] = { type: 'date', value: a.value };
    } else if (t === 'time' && typeof a.value === 'string') {
      out[k] = { type: 'time', value: a.value };
    }
  }
  return out;
}

export function mapSurveyResponse(snap: { id: string; data: () => DocumentData }): SurveyResponse {
  const data = snap.data();
  return {
    surveyId: typeof data.surveyId === 'string' ? data.surveyId : '',
    districtId: typeof data.districtId === 'string' ? data.districtId : '',
    participantUid: typeof data.participantUid === 'string' ? data.participantUid : snap.id,
    participantSnapshot: asProfile(data.participantSnapshot),
    answers: asAnswers(data.answers),
    schemaVersion: 1,
    surveyVersion: typeof data.surveyVersion === 'number' ? data.surveyVersion : 0,
    createdAt: tsToIso(data.createdAt),
    updatedAt: tsToIso(data.updatedAt),
  };
}

export function useSurveyResponses(
  districtId: string | null,
  surveyId: string | null,
): SurveyResponsesState {
  const [state, setState] = useState<SurveyResponsesState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId || !surveyId) {
      setState({
        status: 'error',
        items: [],
        error: new Error('districtId and surveyId are required'),
      });
      return;
    }
    const q = query(
      collection(db, 'districts', districtId, 'surveys', surveyId, 'responses'),
      orderBy('updatedAt', 'desc'),
    );
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(mapSurveyResponse);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId, surveyId]);

  return state;
}
